import React, { useState, useEffect, useCallback, useRef } from 'react';
import Header from '../components/Header';
import { getLibrary, getLibraryCategory, getLibraryFolders, getLibraryItem, searchLibrary } from '../api/client';
import {
  Video, Play, FileText, Sheet, Link as LinkIcon, HelpCircle, Folder, Search as SearchIcon,
  Download, Eye, Calendar, Clock, Mic, Tag, ChevronRight, ChevronLeft, X, Paperclip, Users,
  BookOpen, Scale, Receipt, Euro, TrendingUp, Hammer, Wrench, Shield, MessageCircle, Building,
} from '../components/icons';

const TYPE_LABELS = { video: 'Видео', pdf: 'PDF документ', xlsx: 'Excel таблица', doc: 'Word документ', link: 'Ссылка', qa: 'Вопрос и ответ' };

// file_type → SVG icon
const fileIcon = (type, size) => {
  switch (type) {
    case 'video': return <Play size={size} />;
    case 'xlsx': return <Sheet size={size} />;
    case 'link': return <LinkIcon size={size} />;
    case 'qa': return <HelpCircle size={size} />;
    case 'pdf':
    case 'doc':
    default: return <FileText size={size} />;
  }
};

// category → SVG icon, matched by slug/name keywords (categories are DB-driven, no fixed set)
const CAT_RULES = [
  [/(договор|vertrag|recht|legal|юрид|закон|gesetz)/, Scale],
  [/(смет|kalkul|angebot|estimate|расчет|расчёт)/, Receipt],
  [/(налог|steuer|buchhalt|tax|финанс|geld|invoice|rechnung)/, Euro],
  [/(маркет|market|werbung|реклам|akquise|лид|lead)/, TrendingUp],
  [/(видео|video|курс|обуч|schulung|lekt|урок|webinar|вебинар)/, Video],
  [/(строит|bau|ремонт|handwerk|монтаж|renov)/, Hammer],
  [/(инструмент|tool|werkzeug|gerät|техник)/, Wrench],
  [/(клиент|kunde|customer|команд|team|mitarbeit|персонал)/, Users],
  [/(безопас|sicherheit|schutz|arbeitsschutz|охран)/, Shield],
  [/(вопрос|faq|frage|q&a|qa|ответ)/, MessageCircle],
  [/(дом|haus|объект|immobil|gebäud|здани)/, Building],
  [/(документ|dokument|шаблон|vorlage|blank|бланк|формуляр)/, FileText],
];
const catIcon = (cat, size) => {
  const hay = `${cat.slug || ''} ${cat.name || ''}`.toLowerCase();
  const rule = CAT_RULES.find(([re]) => re.test(hay));
  const Icon = rule ? rule[1] : BookOpen;
  return <Icon size={size} />;
};

// hex → rgba string
const hexA = (color, a) => {
  if (!color || color[0] !== '#') return `rgba(255,204,0,${a})`;
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
};

const LIB_CSS = `
.lib-root { --ease: cubic-bezier(.22,.68,0,1); }
.lib-anim { animation: libIn .45s var(--ease) both; }
@keyframes libIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
.lib-card { transition: transform .18s var(--ease), border-color .25s ease; -webkit-tap-highlight-color: transparent; }
.lib-card:active { transform: scale(.985); }
.lib-root button:active { transform: scale(.97); }
@media (prefers-reduced-motion: reduce) {
  .lib-anim { animation: none !important; }
  .lib-card { transition: none; }
}
`;

export default function LibraryPage() {
  const [screen, setScreen] = useState('main'); // main | category | folder | detail
  const [categories, setCategories] = useState([]);
  const [recent, setRecent] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [folders, setFolders] = useState([]);
  const [unfiledCount, setUnfiledCount] = useState(0);
  const [activeFolder, setActiveFolder] = useState(null);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [lbZoom, setLbZoom] = useState(1);
  const lbStartDist = useRef(null);
  const lbSwipeX = useRef(null);

  const openLightbox = (images, index) => { setLightbox({ images, index }); setLbZoom(1); };
  const lbPrev = () => { if (lightbox && lightbox.index > 0) { setLightbox({ ...lightbox, index: lightbox.index - 1 }); setLbZoom(1); } };
  const lbNext = () => { if (lightbox && lightbox.index < lightbox.images.length - 1) { setLightbox({ ...lightbox, index: lightbox.index + 1 }); setLbZoom(1); } };

  const loadMain = useCallback(() => {
    getLibrary().then(data => {
      setCategories(data.categories || []);
      setRecent(data.recent || []);
    }).catch(console.error);
  }, []);

  useEffect(() => { loadMain(); }, [loadMain]);

  useEffect(() => {
    const onVisible = () => { if (!document.hidden && screen === 'main') loadMain(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loadMain, screen]);

  useEffect(() => {
    if (search.length < 2) { setSearchResults(null); setSearching(false); return; }
    setSearching(true);
    const timer = setTimeout(() => {
      searchLibrary(search).then(data => { setSearchResults(data.items || []); setSearching(false); }).catch(() => { setSearchResults([]); setSearching(false); });
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const openCategory = async (cat) => {
    setActiveCategory(cat);
    try {
      const data = await getLibraryFolders(cat.slug);
      const flds = data.folders || [];
      const unfiled = data.unfiled_count || 0;
      setFolders(flds);
      setUnfiledCount(unfiled);
      if (flds.length === 0 && unfiled > 0) {
        setActiveFolder({ name: cat.name });
        const itemsData = await getLibraryCategory(cat.slug);
        setItems(itemsData.items || []);
        setScreen('folder');
        return;
      }
    } catch (e) {
      setFolders([]);
      setUnfiledCount(0);
    }
    setScreen('category');
  };

  const openFolder = async (folder) => {
    setActiveFolder(folder);
    const data = await getLibraryCategory(activeCategory.slug, `folder_id=${folder.id}`);
    setItems(data.items || []);
    setScreen('folder');
  };

  const openUnfiled = async () => {
    setActiveFolder({ name: 'Прочее' });
    const data = await getLibraryCategory(activeCategory.slug, 'folder_id=none');
    setItems(data.items || []);
    setScreen('folder');
  };

  const goBack = () => {
    if (screen === 'detail') {
      setScreen('folder');
      setActiveItem(null);
    } else if (screen === 'folder') {
      if (folders.length === 0) {
        setScreen('main');
        setActiveCategory(null);
        setActiveFolder(null);
        setItems([]);
      } else {
        setScreen('category');
        setActiveFolder(null);
        setItems([]);
      }
    } else {
      setScreen('main');
      setActiveCategory(null);
      setFolders([]);
    }
  };

  const openItem = async (item) => {
    if (item.description || item.speaker || item.file_type === 'qa') {
      try {
        const full = await getLibraryItem(item.id);
        setActiveItem(full);
      } catch {
        setActiveItem(item);
      }
      setScreen('detail');
    } else if (item.file_url) {
      getLibraryItem(item.id).catch(() => {});
      openLink(item.file_url);
    }
  };

  const openLink = (url) => {
    const fullUrl = url.startsWith('http') ? url : window.location.origin + url;
    if (window.Telegram?.WebApp?.openLink) {
      window.Telegram.WebApp.openLink(fullUrl);
    } else {
      window.open(fullUrl, '_blank');
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('ru', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Back button (SVG chevron)
  const BackBtn = ({ label, delay = 0 }) => (
    <button onClick={goBack} className="lib-anim" style={{
      background: 'none', border: 'none', color: 'var(--accent)', fontSize: 15, fontWeight: 500,
      cursor: 'pointer', padding: '4px 0', fontFamily: 'inherit', textAlign: 'left',
      display: 'inline-flex', alignItems: 'center', gap: 4, animationDelay: `${delay}s`,
    }}>
      <ChevronLeft size={18} /> {label}
    </button>
  );

  // Meta chip with SVG icon
  const Chip = ({ icon, children, accent, tertiary }) => (
    <span style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 9,
      padding: '5px 10px', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6,
      color: accent ? 'var(--accent)' : tertiary ? 'var(--text-tertiary)' : 'var(--text-secondary)',
    }}>
      {React.cloneElement(icon, { size: 14 })} {children}
    </span>
  );

  return (
    <div className="lib-root">
      <style>{LIB_CSS}</style>
      <Header title="Библиотека" />
      <div style={{ padding: '16px 16px 100px', display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* Search */}
        {screen !== 'detail' && (
          <div className="lib-anim" style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', display: 'flex' }}>
              <SearchIcon size={17} />
            </span>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Поиск: договор, смета, налоги..."
              style={{ width: '100%', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px 12px 42px', fontSize: 16, color: 'var(--text-primary)', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
          </div>
        )}

        {/* === SEARCH RESULTS === */}
        {searching && <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-tertiary)', textAlign: 'center', padding: 20 }}>Поиск...</div>}
        {!searching && searchResults !== null && <>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-tertiary)' }}>
            {searchResults.length > 0 ? `Найдено: ${searchResults.length}` : 'Ничего не найдено'}
          </div>
          {searchResults.map((item, i) => (
            <div key={item.id} onClick={() => openItem(item)} className="lib-card lib-anim" style={{
              background: 'var(--bg-card)', borderRadius: 14, padding: '14px 16px',
              border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 14,
              cursor: 'pointer', animationDelay: `${i * 0.03}s`,
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: 11, background: 'var(--accent-dim)', color: 'var(--accent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>{fileIcon(item.file_type, 20)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 3, lineHeight: 1.3 }}>{item.title}</div>
                {item.speaker && <div style={{ fontSize: 12, color: 'var(--accent)', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}><Mic size={12} /> {item.speaker}</div>}
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)', display: 'flex', gap: 8, alignItems: 'center' }}>
                  {item.category_name && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{catIcon({ slug: item.category_slug, name: item.category_name }, 12)} {item.category_name}</span>}
                  {item.duration && <span>{item.duration}</span>}
                </div>
              </div>
              <span style={{ flexShrink: 0, color: 'var(--text-tertiary)', display: 'flex' }}><ChevronRight size={18} /></span>
            </div>
          ))}
        </>}

        {/* === MAIN: Categories Grid === */}
        {screen === 'main' && searchResults === null && <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
            {categories.map((cat, i) => {
              const color = cat.color || 'var(--accent)';
              return (
                <div key={cat.slug} onClick={() => openCategory(cat)} className="lib-card lib-anim" style={{
                  background: 'var(--bg-card)', borderRadius: 16, padding: '18px 15px 16px',
                  border: '1px solid var(--border)', cursor: 'pointer', position: 'relative', overflow: 'hidden',
                  animationDelay: `${0.04 + i * 0.05}s`,
                }}>
                  {/* icon badge (tinted, no glow) */}
                  <div style={{
                    width: 46, height: 46, borderRadius: 13, marginBottom: 12,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: hexA(color, 0.12), color,
                  }}>{catIcon(cat, 24)}</div>
                  <div style={{ fontSize: 14.5, fontWeight: 650, lineHeight: 1.25 }}>{cat.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>{cat.count || 0} материалов</div>
                  <span style={{ position: 'absolute', bottom: 14, right: 13, color: 'var(--text-tertiary)', display: 'flex' }}><ChevronRight size={17} /></span>
                </div>
              );
            })}
          </div>

          {recent.length > 0 && (
            <div className="lib-anim" style={{ animationDelay: '0.18s' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Новое в библиотеке</div>
              <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 4, WebkitOverflowScrolling: 'touch' }}>
                {recent.map(item => {
                  const isVid = item.file_type === 'video' || item.file_type === 'link';
                  return (
                    <div key={item.id} onClick={() => openItem(item)} className="lib-card" style={{
                      background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border)',
                      flexShrink: 0, width: 200, overflow: 'hidden', cursor: 'pointer',
                    }}>
                      <div style={{
                        height: 84, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: isVid ? 'linear-gradient(135deg, #17203a, #0f3460)' : 'linear-gradient(135deg, #26261c, #1a1a12)',
                        color: isVid ? 'var(--teal)' : 'var(--accent)',
                      }}>
                        <span style={{ display: 'flex' }}>{fileIcon(item.file_type, 30)}</span>
                      </div>
                      <div style={{ padding: 11 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.3 }}>{item.title}</div>
                        {item.speaker && <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}><Mic size={11} /> {item.speaker}</div>}
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{item.duration || (item.meta && !item.meta.startsWith('event_') ? item.meta : TYPE_LABELS[item.file_type] || item.file_type)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>}

        {/* === CATEGORY: Folders Grid === */}
        {screen === 'category' && activeCategory && searchResults === null && <>
          <BackBtn label="Библиотека" />

          {(() => {
            const color = activeCategory.color || 'var(--accent)';
            return (
              <div className="lib-anim" style={{ display: 'flex', alignItems: 'center', gap: 14, animationDelay: '0.05s' }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 15, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: hexA(color, 0.12), color,
                }}>{catIcon(activeCategory, 28)}</div>
                <div>
                  <div style={{ fontSize: 21, fontWeight: 700 }}>{activeCategory.name}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    {(folders.reduce((s, f) => s + (f.count || 0), 0) + unfiledCount)} материалов
                  </div>
                </div>
              </div>
            );
          })()}

          {folders.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
              {folders.map((folder, i) => (
                <div key={folder.id} onClick={() => openFolder(folder)} className="lib-card lib-anim" style={{
                  background: 'var(--bg-card)', borderRadius: 14, padding: '15px 14px',
                  border: '1px solid var(--border)', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 12, animationDelay: `${0.1 + i * 0.04}s`,
                }}>
                  <span style={{ color: 'var(--accent)', display: 'flex', flexShrink: 0 }}><Folder size={24} /></span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3, wordBreak: 'break-word' }}>{folder.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{folder.count || 0} шт</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {unfiledCount > 0 && (
            <div onClick={openUnfiled} className="lib-card lib-anim" style={{
              background: 'var(--bg-card)', borderRadius: 14, padding: '14px 16px',
              border: '1px solid var(--border)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 12, animationDelay: '0.2s',
            }}>
              <span style={{ color: 'var(--text-secondary)', display: 'flex' }}><Folder size={22} /></span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>Прочее</div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{unfiledCount} шт</div>
              </div>
              <span style={{ color: 'var(--text-tertiary)', display: 'flex' }}><ChevronRight size={17} /></span>
            </div>
          )}

          {folders.length === 0 && unfiledCount === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}>Пока пусто</div>
          )}
        </>}

        {/* === FOLDER: Files List === */}
        {screen === 'folder' && activeFolder && searchResults === null && <>
          <BackBtn label={activeCategory.name} />

          <div className="lib-anim" style={{ display: 'flex', alignItems: 'center', gap: 14, animationDelay: '0.05s' }}>
            <span style={{ color: 'var(--accent)', display: 'flex' }}><Folder size={32} /></span>
            <div>
              <div style={{ fontSize: 21, fontWeight: 700 }}>{activeFolder.name}</div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{items.length} {items.length === 1 ? 'материал' : 'материалов'}</div>
            </div>
          </div>

          {items.map((item, i) => (
            <div key={item.id} onClick={() => openItem(item)} className="lib-card lib-anim" style={{
              background: 'var(--bg-card)', borderRadius: 14, padding: '14px 16px',
              border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 14,
              cursor: 'pointer', animationDelay: `${0.1 + i * 0.04}s`,
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: 11, flexShrink: 0,
                background: hexA(activeCategory.color, 0.14), color: activeCategory.color || 'var(--accent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>{fileIcon(item.file_type, 20)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 3, lineHeight: 1.3 }}>{item.title}</div>
                {item.speaker && <div style={{ fontSize: 12, color: 'var(--accent)', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}><Mic size={12} /> {item.speaker}</div>}
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)', display: 'flex', gap: 8 }}>
                  {item.duration && <span>{item.duration}</span>}
                  {item.lessons_count && <span>{item.lessons_count} уроков</span>}
                  {item.meta && !item.meta.startsWith('event_') && <span>{item.meta}</span>}
                  {!item.duration && !item.lessons_count && !item.meta && <span>{item.file_type?.toUpperCase()}</span>}
                </div>
              </div>
              <span style={{ flexShrink: 0, color: 'var(--text-tertiary)', display: 'flex' }}><ChevronRight size={18} /></span>
            </div>
          ))}

          {items.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}>Пока пусто</div>
          )}
        </>}

        {/* === DETAIL: Item Detail View === */}
        {screen === 'detail' && activeItem && <>
          <BackBtn label="Назад" />

          {activeItem.file_type === 'qa' && activeItem.question ? (
            /* ===== Q&A DETAIL VIEW ===== */
            <div className="lib-anim">
              {/* Question card */}
              <div style={{ background: 'var(--bg-card)', borderRadius: 14, padding: 16, border: '1px solid var(--border)', borderLeft: '3px solid var(--accent)', marginBottom: 14 }}>
                <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 7, background: 'var(--accent-dim)', color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <HelpCircle size={13} /> Вопрос
                  </span>
                  {activeItem.question.category && (
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 7, background: 'rgba(99,99,102,0.12)', color: 'var(--text-secondary)' }}>
                      {activeItem.question.category}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 15, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{activeItem.question.text}</div>
                {/* Question photos */}
                {activeItem.question.photos && activeItem.question.photos.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                    {activeItem.question.photos.map((p, i) => {
                      const filePath = typeof p === 'string' ? p : p.file_path;
                      const fileName = typeof p === 'string' ? filePath.split('/').pop() : (p.file_name || filePath.split('/').pop());
                      const isImage = /\.(jpg|jpeg|png|webp)$/i.test(filePath);
                      return isImage
                        ? <img key={i} src={filePath} alt="" onClick={() => { const allImgs = activeItem.question.photos.filter(x => /\.(jpg|jpeg|png|webp)$/i.test(typeof x === 'string' ? x : x.file_path)).map(x => typeof x === 'string' ? x : x.file_path); openLightbox(allImgs, allImgs.indexOf(filePath)); }} style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 10, border: '1px solid var(--border)', cursor: 'pointer' }} />
                        : <a key={i} href={filePath} target="_blank" rel="noreferrer" style={{ fontSize: 12, padding: '6px 12px', borderRadius: 8, background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--accent)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}><Paperclip size={13} /> {fileName}</a>;
                    })}
                  </div>
                )}
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 10, display: 'flex', gap: 12 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Users size={13} /> {activeItem.question.author_name}</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Calendar size={13} /> {formatDate(activeItem.question.created_at)}</span>
                </div>
              </div>

              {/* Answers */}
              {activeItem.question.answers && activeItem.question.answers.length > 0 && (
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
                    Ответы ({activeItem.question.answers.length})
                  </div>
                  {activeItem.question.answers.map((a, i) => {
                    const isExp = a.author_role === 'admin' || a.author_role === 'expert';
                    return (
                      <div key={i} style={{
                        background: 'var(--bg-card)', borderRadius: 12, padding: '12px 14px',
                        border: '1px solid var(--border)', marginBottom: 8,
                        borderLeft: isExp ? '3px solid var(--success)' : '3px solid var(--border)',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 600 }}>{a.author_name}</span>
                          {isExp && (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: 'rgba(48,209,88,0.12)', color: 'var(--success)' }}>Эксперт</span>
                          )}
                        </div>
                        <div style={{ fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{a.text}</div>
                        {/* Answer attachments */}
                        {a.attachments && a.attachments.length > 0 && (
                          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                            {a.attachments.map((att, j) => {
                              const isImg = /\.(jpg|jpeg|png|webp)$/i.test(att.file_path);
                              return isImg
                                ? <img key={j} src={att.file_path} alt="" onClick={() => { const allImgs = a.attachments.filter(x => /\.(jpg|jpeg|png|webp)$/i.test(x.file_path)).map(x => x.file_path); openLightbox(allImgs, allImgs.indexOf(att.file_path)); }} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer' }} />
                                : <a key={j} href={att.file_path} target="_blank" rel="noreferrer" style={{ fontSize: 12, padding: '5px 10px', borderRadius: 8, background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--accent)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}><Paperclip size={12} /> {att.file_name || 'Файл'}</a>;
                            })}
                          </div>
                        )}
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6 }}>
                          {formatDate(a.created_at)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Meta */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                {activeItem.views > 0 && <Chip icon={<Eye />} tertiary>{activeItem.views}</Chip>}
                {activeItem.speaker && <Chip icon={<Mic />} accent>{activeItem.speaker}</Chip>}
              </div>
            </div>
          ) : (
            /* ===== REGULAR DETAIL VIEW ===== */
            <>
              {/* Hero */}
              {(() => {
                const isVid = activeItem.file_type === 'video' || activeItem.file_type === 'link';
                const glow = isVid ? 'var(--blue)' : 'var(--accent)';
                return (
                  <div className="lib-anim" style={{
                    background: isVid
                      ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
                      : 'linear-gradient(135deg, #201f16 0%, #2a2a1e 100%)',
                    borderRadius: 18, padding: '34px 20px 30px', textAlign: 'center',
                    border: '1px solid var(--border)',
                  }}>
                    <div style={{
                      width: 72, height: 72, borderRadius: 18, margin: '0 auto 14px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: hexA(isVid ? '#0A84FF' : '#FFCC00', 0.14), color: glow,
                    }}>{fileIcon(activeItem.file_type, 34)}</div>
                    <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5, color: 'var(--text-secondary)' }}>
                      {TYPE_LABELS[activeItem.file_type] || activeItem.file_type?.toUpperCase()}
                    </div>
                  </div>
                );
              })()}

              {/* Title & Speaker */}
              <div className="lib-anim" style={{ animationDelay: '0.06s' }}>
                <div style={{ fontSize: 21, fontWeight: 700, lineHeight: 1.3, marginBottom: 6 }}>{activeItem.title}</div>
                {activeItem.speaker && (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    background: 'var(--accent-dim)', borderRadius: 10, padding: '6px 12px', marginTop: 4, color: 'var(--accent)',
                  }}>
                    <Mic size={16} />
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{activeItem.speaker}</span>
                  </div>
                )}
              </div>

              {/* Meta chips */}
              <div className="lib-anim" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, animationDelay: '0.12s' }}>
                {activeItem.duration && <Chip icon={<Clock />}>{activeItem.duration}</Chip>}
                {activeItem.lessons_count > 0 && <Chip icon={<BookOpen />}>{activeItem.lessons_count} уроков</Chip>}
                {activeItem.meta && !activeItem.meta.startsWith('event_') && <Chip icon={<Tag />}>{activeItem.meta}</Chip>}
                {activeItem.views > 0 && <Chip icon={<Eye />} tertiary>{activeItem.views}</Chip>}
                {activeItem.created_at && <Chip icon={<Calendar />} tertiary>{formatDate(activeItem.created_at)}</Chip>}
              </div>

              {/* Description */}
              {activeItem.description && (
                <div className="lib-anim" style={{
                  background: 'var(--bg-card)', borderRadius: 16, padding: '16px 18px',
                  border: '1px solid var(--border)', animationDelay: '0.18s',
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>О материале</div>
                  <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text-primary)', whiteSpace: 'pre-line' }}>
                    {activeItem.description}
                  </div>
                </div>
              )}

              {/* Open button */}
              {activeItem.file_url && (
                <button onClick={() => openLink(activeItem.file_url)} className="lib-anim" style={{
                  width: '100%', padding: '16px 20px',
                  background: 'var(--accent)', color: '#000', border: 'none',
                  borderRadius: 14, fontSize: 16, fontWeight: 700, cursor: 'pointer',
                  fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  animationDelay: '0.24s',
                }}>
                  {activeItem.file_type === 'video' || activeItem.file_type === 'link' ? <Play size={19} /> : <Download size={19} />}
                  {activeItem.file_type === 'video' || activeItem.file_type === 'link' ? 'Смотреть' : 'Скачать'}
                  <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.65 }}>
                    {TYPE_LABELS[activeItem.file_type] || ''}
                  </span>
                </button>
              )}
            </>
          )}
        </>}
      </div>

      {/* Lightbox gallery */}
      {lightbox && (
        <div onClick={() => { setLightbox(null); setLbZoom(1); }}
          onTouchStart={e => {
            if (e.touches.length === 1) { lbSwipeX.current = e.touches[0].clientX; }
            if (e.touches.length === 2) { const dx = e.touches[0].clientX - e.touches[1].clientX; const dy = e.touches[0].clientY - e.touches[1].clientY; lbStartDist.current = Math.hypot(dx, dy); lbSwipeX.current = null; }
          }}
          onTouchMove={e => {
            if (e.touches.length === 2 && lbStartDist.current) { const dx = e.touches[0].clientX - e.touches[1].clientX; const dy = e.touches[0].clientY - e.touches[1].clientY; const dist = Math.hypot(dx, dy); const scale = dist / lbStartDist.current; setLbZoom(z => Math.min(5, Math.max(0.5, z * (scale > 1 ? 1.02 : 0.98)))); lbStartDist.current = dist; e.preventDefault(); }
          }}
          onTouchEnd={e => {
            if (lbSwipeX.current !== null && e.changedTouches.length === 1 && lbZoom <= 1) {
              const diff = e.changedTouches[0].clientX - lbSwipeX.current;
              if (diff > 60) lbPrev();
              else if (diff < -60) lbNext();
            }
            lbSwipeX.current = null; lbStartDist.current = null;
          }}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.92)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16, cursor: 'pointer', touchAction: 'none',
          }}>
          <button onClick={e => { e.stopPropagation(); setLightbox(null); setLbZoom(1); }} style={{
            position: 'absolute', top: 16, right: 16, background: 'rgba(60,60,60,0.8)',
            border: 'none', borderRadius: '50%', width: 36, height: 36,
            color: '#FFCC00', cursor: 'pointer', display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 2,
          }}><X size={18} /></button>
          {lightbox.images.length > 1 && (
            <div style={{ position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', color: '#FFCC00', fontSize: 14, fontWeight: 700 }}>
              {lightbox.index + 1} / {lightbox.images.length}
            </div>
          )}
          {lightbox.images.length > 1 && lightbox.index > 0 && (
            <button onClick={e => { e.stopPropagation(); lbPrev(); }} style={{
              position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(60,60,60,0.8)', border: 'none', borderRadius: '50%',
              width: 40, height: 40, color: '#FFCC00', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2,
            }}><ChevronLeft size={22} /></button>
          )}
          {lightbox.images.length > 1 && lightbox.index < lightbox.images.length - 1 && (
            <button onClick={e => { e.stopPropagation(); lbNext(); }} style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(60,60,60,0.8)', border: 'none', borderRadius: '50%',
              width: 40, height: 40, color: '#FFCC00', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2,
            }}><ChevronRight size={22} /></button>
          )}
          <div onClick={e => e.stopPropagation()} style={{ overflow: 'auto', maxWidth: '100%', maxHeight: '85vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={lightbox.images[lightbox.index]} alt="" style={{
              transform: `scale(${lbZoom})`, transformOrigin: 'center center',
              maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain', borderRadius: 8,
              transition: 'transform 0.1s ease',
            }} />
          </div>
          <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', bottom: 24, display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={() => setLbZoom(z => Math.max(0.5, z - 0.3))} style={lbBtnStyle}>−</button>
            <button onClick={() => setLbZoom(1)} style={{ ...lbBtnStyle, fontSize: 12, minWidth: 44 }}>{Math.round(lbZoom * 100)}%</button>
            <button onClick={() => setLbZoom(z => Math.min(5, z + 0.3))} style={lbBtnStyle}>+</button>
            <button onClick={() => { const src = lightbox.images[lightbox.index]; const url = new URL(src, window.location.origin).href; const fileName = src.split('/').pop() || 'photo.jpg'; if (window.Telegram?.WebApp?.downloadFile) { window.Telegram.WebApp.downloadFile({ url, file_name: fileName }); } else { window.open(url, '_blank'); } }} style={{ ...lbBtnStyle, padding: '10px 20px' }}>Скачать</button>
          </div>
        </div>
      )}
    </div>
  );
}

const lbBtnStyle = {
  background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 10,
  padding: '10px 14px', fontSize: 18, color: '#fff', fontWeight: 600, cursor: 'pointer',
};
